// Block Map: the animated dot world map (Figma 275:36431 default, 275:15152 interactive states).
// The dots come from the SVG an editor picks on the block: the server parses it (squares ->
// dots, <g class="EU">/"NA"/... -> region codes) and serves it from api/block-map/dots/{mediaKey}
// (data-dots-src) - see Features/Blocks/BlockMap/README.md.
//
// Rendering is a single <canvas>, not one DOM element per dot - this matters at ~3,000 dots. Don't
// reintroduce per-dot SVG/DOM elements without re-checking performance (confirmed by the original
// prototype's own notes).

import { countText } from "../i18n/count-text";
import { type Edge, slideFillIn, slideFillOut } from "./button-hover";

// Selection fill timing (see animateSelection): SWITCH_MS when moving between rows (old row out,
// new row in, at once), DEFAULT_FILL_MS for a first selection or a deselect.
const SWITCH_MS = 140;
const DEFAULT_FILL_MS = 100;

interface ContinentStat {
    continentCode: string;
    continentName: string;
    companyCount: number;
    // Companies page filtered to this continent - null when there's no companies page.
    companiesUrl: string | null;
}

// One server-rendered continent row (BlockMap.cshtml). Its "View companies" arrow is shown by
// block-map.css once the selection fill has arrived at the row (data-filled, see animateSelection).
interface ContinentRow {
    row: HTMLElement;
    toggle: HTMLButtonElement;
}

// [x, y, regionCode] - regionCode is the SVG group the dot sits in, or null.
type RawDotEntry = [number, number, string | null];

interface DotPayload {
    width: number;
    height: number;
    cellSize: number;
    dots: RawDotEntry[];
}

interface Dot {
    x: number;
    y: number;
    // The dot's region code, only set (non-null) when that continent is also present in
    // continentByCode (i.e. published in Umbraco), so it's the "is this dot actually interactive"
    // flag as well as the grouping itself.
    region: string | null;
    scale: number;
    // ms after the reveal starts that this dot begins to pop in (see BUILD_DURATION).
    appearAt: number;
}

interface Rgb {
    r: number;
    g: number;
    b: number;
}

interface Bounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

// Fallbacks only - the real colors and dimming come from block-map.css (--block-map-dot,
// --block-map-dot-active, --block-map-dot-dimmed-opacity), read when drawing until they're there
// (components.css can arrive after init - startup.ts gives up waiting after a few seconds).
const FALLBACK_COLOR_DEFAULT: Rgb = { r: 0x11, g: 0x56, b: 0x96 };
const FALLBACK_COLOR_ACTIVE: Rgb = { r: 0x9d, g: 0x3d, b: 0x1e };
const FALLBACK_DIMMED_OPACITY = 0.25;
const ACTIVE_DOT_SCALE = 1.35;

// HOVER_RADIUS below is tuned for a 1000-unit-wide map; it's scaled by the
// actual SVG's width / REFERENCE_WIDTH so the feel doesn't depend on the SVG's own units.
const REFERENCE_WIDTH = 1000;

// Same breakpoint as block-map.css: on mobile the tooltip is hidden (the rows show the counts).
const MOBILE_QUERY = "(max-width: 767px)";

// Reveal (2026-10-08), once the map scrolls into view: the dots pop in one by one in random order
// over BUILD_DURATION ms, few at first and ever more towards the end ("start slow, end fast") -
// each dot's start time is a random number pushed late by a power curve (BUILD_EASE_POWER: the
// share of dots started grows as time^power). Each dot grows in over DOT_POP_DURATION with a plain
// ease-out, and the last one finishes exactly at BUILD_DURATION. Only the blue dots build up: the
// red presence-region dots are already there in full from the start (shown as soon as the data
// loads, before the reveal). Hover is ignored until it's done (see
// previewRegion).
const BUILD_DURATION = 1000;
const BUILD_EASE_POWER = 3;
const DOT_POP_DURATION = 200;

// How close (in REFERENCE_WIDTH units) the pointer must be to a region dot on the map itself for
// hovering the map to highlight that dot's region.
const HOVER_RADIUS = 24;

function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
}

function clamp01(x: number): number {
    return Math.max(0, Math.min(1, x));
}

// Resolves any CSS color string (hex, rgb(), named...) to RGB by letting a canvas context
// normalize it - fillStyle reads back as "#rrggbb" for opaque colors.
function parseCssColor(ctx: CanvasRenderingContext2D, value: string, fallback: Rgb): Rgb {
    const trimmed = value.trim();
    if (!trimmed) {
        return fallback;
    }
    ctx.fillStyle = "#000";
    ctx.fillStyle = trimmed;
    const normalized = typeof ctx.fillStyle === "string" ? ctx.fillStyle : "";
    const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(normalized);
    if (!match) {
        return fallback;
    }
    const [, r = "", g = "", b = ""] = match;
    return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16) };
}

class BlockMap {
    private readonly section: HTMLElement;
    private readonly wrap: HTMLElement;
    private readonly canvas: HTMLCanvasElement;
    private readonly ctx: CanvasRenderingContext2D;
    private readonly tooltip: HTMLElement;
    private readonly tooltipName: HTMLElement;
    private readonly tooltipStat: HTMLElement;
    private readonly tooltipLink: HTMLAnchorElement | null;
    private readonly continentByCode = new Map<string, ContinentStat>();
    private readonly rowByCode = new Map<string, ContinentRow>();
    // Continent codes in list (DOM) order - the selection fill passes through them in this order.
    private readonly rowOrder: string[] = [];
    private selectionToken = 0;

    private width = REFERENCE_WIDTH;
    private height = 500;
    private dotSize = 4.4;
    // width / REFERENCE_WIDTH - see REFERENCE_WIDTH.
    private unitScale = 1;
    private colorDefault = FALLBACK_COLOR_DEFAULT;
    private colorActive = FALLBACK_COLOR_ACTIVE;
    private dimmedOpacity = FALLBACK_DIMMED_OPACITY;
    private colorsFromCss = false;
    private readonly mobileQuery = window.matchMedia(MOBILE_QUERY);

    private dots: Dot[] = [];
    private regionBounds = new Map<string, Bounds>();
    private dataReady = false;
    private pendingReveal = false;
    private revealed = false;
    // Reduced motion / no IntersectionObserver: the whole map, red included, straight away.
    private revealInstantly = false;
    // True while the build-up runs - hover previews are ignored until it's done.
    private revealAnimating = false;
    private animating = false;
    private animationStart: number | null = null;
    private hoveredRegion: string | null = null;
    private activeRegion: string | null = null;
    private lastDrawnHover: string | null | undefined = undefined;
    private lastDrawnActive: string | null | undefined = undefined;

    constructor(section: HTMLElement, continents: ContinentStat[]) {
        this.section = section;
        this.wrap = section.querySelector<HTMLElement>("[data-block-map-wrap]")!;
        this.canvas = section.querySelector<HTMLCanvasElement>("[data-block-map-canvas]")!;
        this.ctx = this.canvas.getContext("2d")!;
        this.tooltip = section.querySelector<HTMLElement>("[data-block-map-tooltip]")!;
        this.tooltipName = section.querySelector<HTMLElement>("[data-block-map-tooltip-name]")!;
        this.tooltipStat = section.querySelector<HTMLElement>("[data-block-map-tooltip-stat]")!;
        this.tooltipLink = section.querySelector<HTMLAnchorElement>("[data-block-map-tooltip-link]");

        continents.forEach((continent) => this.continentByCode.set(continent.continentCode, continent));

        this.wireContinentRows();
        this.bindCanvasEvents();
        this.observeSize();
        void this.loadDots();
        this.observeReveal();
    }

    // Until block-map.css has applied (its custom properties are empty before), the fallbacks are
    // used and this tries again on the next draw.
    private readColors(): void {
        const styles = getComputedStyle(this.section);
        const dot = styles.getPropertyValue("--block-map-dot");
        if (!dot.trim()) {
            return;
        }
        this.colorDefault = parseCssColor(this.ctx, dot, FALLBACK_COLOR_DEFAULT);
        this.colorActive = parseCssColor(this.ctx, styles.getPropertyValue("--block-map-dot-active"), FALLBACK_COLOR_ACTIVE);
        const dimmed = Number.parseFloat(styles.getPropertyValue("--block-map-dot-dimmed-opacity"));
        this.dimmedOpacity = Number.isFinite(dimmed) ? dimmed : FALLBACK_DIMMED_OPACITY;
        this.colorsFromCss = true;
    }

    // The canvas's backing store follows its displayed size times devicePixelRatio - not the
    // SVG's own units, which made a phone's canvas several times bigger than its screen (~39 MB
    // at 390px and dpr 3) and a wide screen's smaller (blurry dots). Drawing stays in SVG units:
    // the transform scales them to the backing store. Also keeps a shown tooltip on its region
    // when the map resizes, rotates or crosses the mobile breakpoint.
    private observeSize(): void {
        const resize = () => {
            this.sizeCanvas();
            this.repositionTooltip();
        };
        new ResizeObserver(resize).observe(this.canvas);
        this.mobileQuery.addEventListener("change", () => this.repositionTooltip());
        this.watchPixelRatio();
    }

    // A zoom or a move to another screen changes devicePixelRatio without resizing anything.
    private watchPixelRatio(): void {
        window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`).addEventListener("change", () => {
            this.sizeCanvas();
            this.watchPixelRatio();
        }, { once: true });
    }

    private sizeCanvas(): void {
        const displayWidth = this.canvas.clientWidth;
        if (!this.dataReady || displayWidth === 0) {
            return;
        }
        const dpr = window.devicePixelRatio || 1;
        const backingWidth = Math.max(1, Math.round(displayWidth * dpr));
        const backingHeight = Math.max(1, Math.round(displayWidth * (this.height / this.width) * dpr));
        if (this.canvas.width !== backingWidth || this.canvas.height !== backingHeight) {
            // Setting the size clears the canvas and resets the context.
            this.canvas.width = backingWidth;
            this.canvas.height = backingHeight;
        }
        this.ctx.setTransform(backingWidth / this.width, 0, 0, backingHeight / this.height, 0, 0);
        this.redraw();
    }

    // A single frame even when nothing changed (requestDraw stops once the state is drawn) - the
    // canvas was just cleared.
    private redraw(): void {
        this.lastDrawnHover = undefined;
        this.requestDraw();
    }

    private repositionTooltip(): void {
        const shown = this.activeRegion ?? this.hoveredRegion;
        if (shown && this.tooltip.classList.contains("is-visible")) {
            this.showTooltip(shown);
        }
    }

    private async loadDots(): Promise<void> {
        const src = this.section.dataset.dotsSrc;
        if (!src) {
            return;
        }

        try {
            const response = await fetch(src);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const payload = (await response.json()) as DotPayload;
            this.applyDotPayload(payload);
        } catch (error) {
            console.error("[block-map] failed to load dot data", error);
        }
    }

    private applyDotPayload(payload: DotPayload): void {
        this.width = payload.width;
        this.height = payload.height;
        this.dotSize = payload.cellSize;
        this.unitScale = payload.width / REFERENCE_WIDTH;

        this.dots = payload.dots.map(([x, y, svgRegion]) => {
            const region = svgRegion && this.continentByCode.has(svgRegion) ? svgRegion : null;
            return {
                x,
                y,
                region,
                scale: 0,
                // Red (presence) dots are there, fully grown, from the start - only the blue ones
                // build up (see BUILD_DURATION).
                appearAt: region
                    ? -Infinity
                    : (BUILD_DURATION - DOT_POP_DURATION) * Math.pow(Math.random(), 1 / BUILD_EASE_POWER),
            };
        });

        this.regionBounds = computeRegionBounds(this.dots, this.dotSize);

        // Normally already rendered by BlockMap.cshtml from the same SVG (no layout shift).
        this.canvas.style.aspectRatio = `${this.width} / ${this.height}`;

        this.dataReady = true;
        // Draws the red dots right away; the blue ones wait for the reveal.
        this.sizeCanvas();
        if (this.pendingReveal) {
            this.pendingReveal = false;
            this.reveal();
        }
    }

    // The rows are server-rendered (BlockMap.cshtml) - this only wires them up.
    private wireContinentRows(): void {
        this.section.querySelectorAll<HTMLElement>("[data-block-map-continent]").forEach((row) => {
            const code = row.dataset.blockMapContinent;
            const toggle = row.querySelector<HTMLButtonElement>("[data-block-map-continent-toggle]");
            if (!code || !toggle) {
                return;
            }

            // A continent with companies but no matching dots in the SVG (not grouped there yet)
            // still gets a row with its count and link - it just won't highlight anything on the
            // map. Checked as a function (not captured once) because the dots load asynchronously
            // and are still empty when the rows are wired up.
            const hasRegion = () => this.dots.some((dot) => dot.region === code);

            toggle.addEventListener("click", () => this.selectRegion(this.activeRegion === code ? null : code));
            toggle.addEventListener("mouseenter", () => {
                if (!this.activeRegion && hasRegion()) {
                    this.previewRegion(code);
                }
            });
            toggle.addEventListener("mouseleave", () => {
                if (!this.activeRegion) {
                    this.previewRegion(null);
                }
            });
            toggle.addEventListener("focus", () => {
                if (!this.activeRegion && hasRegion()) {
                    this.previewRegion(code);
                }
            });
            toggle.addEventListener("blur", () => {
                if (!this.activeRegion) {
                    this.previewRegion(null);
                }
            });

            this.rowByCode.set(code, { row, toggle });
            this.rowOrder.push(code);
        });
    }

    private bindCanvasEvents(): void {
        this.canvas.addEventListener("mousemove", (event) => {
            const { canvasX, canvasY } = this.toCanvasPoint(event);
            const region = this.regionAtPoint(canvasX, canvasY);
            // Clickable with a selection too (another region selects it, the selected one opens
            // its companies) - only the hover preview is off then.
            this.canvas.style.cursor = region ? "pointer" : "default";

            if (this.activeRegion) {
                return;
            }
            if (region !== this.hoveredRegion) {
                this.previewRegion(region);
            }
        });

        // The tooltip is clickable (its "View companies" link) and sits on top of the canvas, so
        // moving the pointer from the map onto the card - or back - must not end the hover.
        this.canvas.addEventListener("mouseleave", (event) => {
            this.canvas.style.cursor = "default";
            if (this.activeRegion || this.tooltip.contains(event.relatedTarget as Node | null)) {
                return;
            }
            this.previewRegion(null);
        });

        this.tooltip.addEventListener("mouseleave", (event) => {
            if (this.activeRegion || event.relatedTarget === this.canvas) {
                return;
            }
            this.previewRegion(null);
        });

        // The map is a big button for the current selection: clicking a region selects it,
        // clicking the already-selected region goes to its filtered companies page.
        this.canvas.addEventListener("click", (event) => {
            const { canvasX, canvasY } = this.toCanvasPoint(event);
            const region = this.regionAtPoint(canvasX, canvasY);
            if (!region) {
                return;
            }
            const url = this.continentByCode.get(region)?.companiesUrl;
            if (region === this.activeRegion && url) {
                window.location.assign(url);
                return;
            }
            this.selectRegion(region);
        });
    }

    private toCanvasPoint(event: MouseEvent): { canvasX: number; canvasY: number } {
        const rect = this.canvas.getBoundingClientRect();
        return {
            canvasX: (event.clientX - rect.left) * (this.width / rect.width),
            canvasY: (event.clientY - rect.top) * (this.height / rect.height),
        };
    }

    // Hovering the map itself is the primary way to explore presence regions; the continent list
    // is a keyboard/touch-friendly complement to the same action.
    private regionAtPoint(canvasX: number, canvasY: number): string | null {
        let nearestRegion: string | null = null;
        let nearestDistance = Infinity;
        for (const dot of this.dots) {
            if (!dot.region) {
                continue;
            }
            const distance = Math.hypot(dot.x - canvasX, dot.y - canvasY);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestRegion = dot.region;
            }
        }
        return nearestDistance <= HOVER_RADIUS * this.unitScale ? nearestRegion : null;
    }

    private previewRegion(continentCode: string | null): void {
        // No hover highlight while the reveal is running (clearing one is still fine).
        if (continentCode && this.revealAnimating) {
            return;
        }
        this.hoveredRegion = continentCode;
        if (continentCode && !this.activeRegion) {
            this.showTooltip(continentCode);
        } else if (!this.activeRegion) {
            this.hideTooltip();
        }
        this.requestDraw();
    }

    // null = deselect. Updates the state (rows, tooltip, map) immediately; the list's selection
    // fill then animates there on its own schedule (animateSelection).
    private selectRegion(continentCode: string | null): void {
        const previous = this.activeRegion;
        this.activeRegion = continentCode;
        this.hoveredRegion = continentCode;
        this.syncRows();
        if (continentCode) {
            this.showTooltip(continentCode);
        } else {
            this.hideTooltip();
        }
        this.requestDraw();
        this.animateSelection(previous, continentCode);
    }

    private syncRows(): void {
        this.rowByCode.forEach(({ row, toggle }, code) => {
            const isActive = code === this.activeRegion;
            // data-active is the selection itself (the arrow waits for data-filled, see
            // animateSelection); aria-pressed is what tells a screen reader this is a toggle and
            // its on/off state.
            row.dataset.active = String(isActive);
            toggle.setAttribute("aria-pressed", String(isActive));
        });
    }

    // The selection fill (each row's ::before, slid by button-hover.ts's slideFillIn/Out - same
    // mechanics as a button hover) moves like a Press Release list item's hover, but on click:
    // - first selection: slides in from the top;
    // - deselect: slides out through the top;
    // - row i -> row k: row i slides out toward k while row k slides in from the side the
    //   selection came from (going down: out through the bottom, in from the top; going up: the
    //   reverse), both at once. Mobile has no hover to take a direction from, so the direction
    //   comes from where the previous selection was. The rows in between don't move (per the
    //   designer - direction only, no pass-through).
    // A newer selection cancels a still-pending frame (selectionToken).
    private animateSelection(previous: string | null, next: string | null): void {
        const token = ++this.selectionToken;

        const rows = this.rowOrder.map((code) => this.rowByCode.get(code)!.row);
        const from = previous ? this.rowOrder.indexOf(previous) : -1;
        const to = next ? this.rowOrder.indexOf(next) : -1;
        if (from === to) {
            return;
        }

        // data-filled marks the row the fill has actually ARRIVED at (vs. data-active, which
        // changes on click) - block-map.css shows the arrow off it, so the arrow appears with
        // the fill behind it rather than on a still-white row while the fill is on its way.
        const fillIn = (row: HTMLElement, edge: Edge) => {
            row.dataset.filled = "true";
            slideFillIn(row, edge);
        };
        const fillOut = (row: HTMLElement, edge: Edge) => {
            delete row.dataset.filled;
            slideFillOut(row, edge);
        };
        // A row still filled from an interrupted switch (a second tap before the first one's
        // slide-out frame ran) is neither the current nor the next selection - clear it too.
        rows.forEach((row, index) => {
            if (index !== from && index !== to && row.dataset.filled === "true") {
                setFillTiming(row, DEFAULT_FILL_MS, "ease-out");
                fillOut(row, "top");
            }
        });

        if (to === -1) {
            setFillTiming(rows[from]!, DEFAULT_FILL_MS, "ease-out");
            fillOut(rows[from]!, "top");
            return;
        }
        if (from === -1) {
            setFillTiming(rows[to]!, DEFAULT_FILL_MS, "ease-out");
            fillIn(rows[to]!, "top");
            return;
        }

        const goingDown = to > from;
        const entry: Edge = goingDown ? "top" : "bottom";
        const exit: Edge = goingDown ? "bottom" : "top";
        const leaving = rows[from]!;
        const arriving = rows[to]!;
        setFillTiming(leaving, SWITCH_MS, "ease-in-out");
        setFillTiming(arriving, SWITCH_MS, "ease-in-out");

        // slideFillIn snaps the arriving fill to its start edge now and starts moving it on the
        // next frame - start the leaving row's slide-out on that same frame so both move together.
        fillIn(arriving, entry);
        requestAnimationFrame(() => {
            if (this.selectionToken === token) {
                fillOut(leaving, exit);
            }
        });
    }

    private showTooltip(continentCode: string): void {
        const continent = this.continentByCode.get(continentCode);
        const bounds = this.regionBounds.get(continentCode);
        if (!continent || !bounds) {
            this.hideTooltip();
            return;
        }

        this.tooltipName.textContent = continent.continentName;
        this.tooltipStat.textContent = countText(this.tooltipStat, continent.companyCount);

        // "View companies" is shown whenever the card is (hover or click) - set before measuring
        // below, since the link changes the card's size.
        if (this.tooltipLink) {
            this.tooltipLink.hidden = !continent.companiesUrl;
            if (continent.companiesUrl) {
                this.tooltipLink.href = continent.companiesUrl;
            }
        }

        if (this.mobileQuery.matches) {
            // Pinned by block-map.css on mobile - clear any desktop inline position.
            this.tooltip.style.left = "";
            this.tooltip.style.top = "";
        } else {
            // Anchored at the region's top-left corner; block-map.css lifts the card above it
            // (translateY(-100%)). Clamped so the card stays inside the map horizontally and
            // doesn't rise above the block's top edge.
            const scaleX = this.wrap.clientWidth / this.width;
            const scaleY = this.wrap.clientHeight / this.height;
            const maxLeft = Math.max(0, this.wrap.clientWidth - this.tooltip.offsetWidth);
            const left = Math.min(Math.max(bounds.minX * scaleX, 0), maxLeft);
            const top = Math.max(bounds.minY * scaleY, this.tooltip.offsetHeight);
            this.tooltip.style.left = `${left}px`;
            this.tooltip.style.top = `${top}px`;
        }
        this.tooltip.classList.add("is-visible");
    }

    private hideTooltip(): void {
        this.tooltip.classList.remove("is-visible");
    }

    private observeReveal(): void {
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

        if (prefersReducedMotion) {
            this.showAllInstantly();
        } else if ("IntersectionObserver" in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.reveal();
                        observer.disconnect();
                    }
                });
            }, { threshold: 0.2 });
            observer.observe(this.section);
        } else {
            this.showAllInstantly();
        }
    }

    private reveal(): void {
        if (!this.dataReady) {
            this.pendingReveal = true;
            return;
        }
        if (this.revealed) {
            return;
        }
        this.revealed = true;
        this.animationStart = null;
        this.requestDraw();
    }

    private showAllInstantly(): void {
        this.revealInstantly = true;
        this.revealed = true;
        this.animationStart = null;
        if (this.dataReady) {
            this.requestDraw();
        }
    }

    private requestDraw(): void {
        if (this.animating) {
            return;
        }
        this.animating = true;
        requestAnimationFrame((now) => this.draw(now));
    }

    private draw(now: number): void {
        if (this.revealed && this.animationStart === null) {
            this.animationStart = now;
        }
        // Nothing is drawn before the reveal; after it, elapsed drives the build-up (BUILD_DURATION).
        const elapsed = !this.revealed ? -1
            : this.revealInstantly ? Infinity
            : now - (this.animationStart ?? now);
        this.revealAnimating = this.revealed && elapsed < BUILD_DURATION;

        if (!this.colorsFromCss) {
            this.readColors();
        }

        this.ctx.clearRect(0, 0, this.width, this.height);

        // A continent with companies but no dots in the SVG can still be selected in the list -
        // it highlights nothing, rather than dimming the whole map.
        const highlighted = this.activeRegion || this.hoveredRegion;
        const activeKey = highlighted && this.regionBounds.has(highlighted) ? highlighted : null;

        for (const dot of this.dots) {
            const growT = clamp01((elapsed - dot.appearAt) / DOT_POP_DURATION);
            if (growT <= 0) {
                continue;
            }

            let size = this.dotSize * easeOutCubic(growT);
            let alpha = 1;
            let fill = dot.region ? this.colorActive : this.colorDefault;

            if (activeKey) {
                if (dot.region === activeKey) {
                    size *= ACTIVE_DOT_SCALE;
                    fill = this.colorActive;
                    alpha = 1;
                } else {
                    alpha = this.dimmedOpacity;
                }
            }

            this.ctx.fillStyle = `rgba(${fill.r}, ${fill.g}, ${fill.b}, ${alpha})`;
            this.ctx.fillRect(dot.x - size / 2, dot.y - size / 2, size, size);
        }

        const hoverChanged = this.hoveredRegion !== this.lastDrawnHover;
        const activeChanged = this.activeRegion !== this.lastDrawnActive;

        if (this.revealAnimating || hoverChanged || activeChanged) {
            this.lastDrawnHover = this.hoveredRegion;
            this.lastDrawnActive = this.activeRegion;
            requestAnimationFrame((next) => this.draw(next));
        } else {
            this.lastDrawnHover = this.hoveredRegion;
            this.lastDrawnActive = this.activeRegion;
            this.animating = false;
        }
    }
}

// Per-step duration/easing for a row's selection fill - read by block-map.css's ::before
// transition (--block-map-fill-duration/--block-map-fill-ease).
function setFillTiming(row: HTMLElement, durationMs: number, easing: string): void {
    row.style.setProperty("--block-map-fill-duration", `${durationMs}ms`);
    row.style.setProperty("--block-map-fill-ease", easing);
}

// Outer edges of each region's dots as drawn when highlighted (enlarged by ACTIVE_DOT_SCALE),
// in canvas units - the tooltip anchors to the top-left corner.
function computeRegionBounds(dots: Dot[], dotSize: number): Map<string, Bounds> {
    const half = (dotSize * ACTIVE_DOT_SCALE) / 2;
    const bounds = new Map<string, Bounds>();
    dots.forEach((dot) => {
        if (!dot.region) {
            return;
        }
        const b = bounds.get(dot.region);
        if (!b) {
            bounds.set(dot.region, { minX: dot.x - half, minY: dot.y - half, maxX: dot.x + half, maxY: dot.y + half });
            return;
        }
        b.minX = Math.min(b.minX, dot.x - half);
        b.minY = Math.min(b.minY, dot.y - half);
        b.maxX = Math.max(b.maxX, dot.x + half);
        b.maxY = Math.max(b.maxY, dot.y + half);
    });
    return bounds;
}

export function initBlockMap(): void {
    document.querySelectorAll<HTMLElement>("[data-block-map]").forEach((section) => {
        let continents: ContinentStat[] = [];
        try {
            continents = JSON.parse(section.dataset.continents || "[]");
        } catch (error) {
            console.error("[block-map] failed to parse continent data", error);
            return;
        }

        new BlockMap(section, continents);
    });
}
